'use client';
import Link from "next/link";
import { useRef, useState, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { Check, X, CheckCircle2, Shield, Crown, Loader2, AlertCircle, MessageSquare, Sparkles } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import BrandSafeText from "./BrandSafeText";

/* `key` is what the API and the register-school link expect, so it never
   moves with the language. The visible label and the "/month" suffix come
   from `pricing.cycles`, index-aligned with this list. */
const BILLING_CYCLES = ['monthly', 'threeMonths', 'sixMonths', 'yearly'];

// Stable, data-derived id fragment for test locators (never array index).
const slug = (value) =>
    String(value ?? '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

function getPriceDisplay(plan, cycle) {
    const pricing = plan.pricing?.[cycle];
    if (!pricing) return { price: 0, salePrice: null, onSale: false };
    return { price: pricing.price, salePrice: pricing.salePrice, onSale: pricing.onSale };
}

// Pick exactly 3 plans for the landing page:
// 1. Lowest-price plan (not recommended, not most popular)
// 2. Recommended plan
// 3. Most Popular plan
// Falls back gracefully if some roles don't exist.
function selectLandingPlans(allPlans) {
    if (!allPlans || allPlans.length === 0) return [];

    const popular = allPlans.find(p => p.isMostPopular);
    const recommended = allPlans.find(p => p.isRecommended && !p.isMostPopular);

    // Sort remaining by monthly price ascending to get the cheapest base plan
    const others = allPlans
        .filter(p => !p.isMostPopular && !p.isRecommended)
        .sort((a, b) => (a.pricing?.monthly?.price ?? 0) - (b.pricing?.monthly?.price ?? 0));

    const base = others[0] || allPlans.find(p => p.isRecommended && p.isMostPopular) || allPlans[0];

    // Build ordered array: base → recommended → popular (deduplicate)
    const picked = [];
    const seen = new Set();

    for (const p of [base, recommended, popular]) {
        if (p && !seen.has(p._id)) {
            seen.add(p._id);
            picked.push(p);
        }
    }

    return picked;
}

export default function Pricing() {
    const { t, tv, tf } = useLanguage();
    const cycleCopy = tv("pricing.cycles");
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [billingCycle, setBillingCycle] = useState('monthly');

    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-20px" });

    const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || '';

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/subscriptions/plans`);
                if (!res.ok) throw new Error('Failed to fetch plans');
                const data = await res.json();
                if (data.success) {
                    setPlans(data.plans);
                } else {
                    setError(t('pricing.errorNoPlans'));
                }
            } catch {
                setError(t('pricing.errorLoad'));
            } finally {
                setLoading(false);
            }
        };
        fetchPlans();
    }, []);

    const displayPlans = selectLandingPlans(plans);

    return (
        <section ref={ref} id="pricing" data-testid="pricing-section-root" className="relative w-full scroll-mt-24 py-12 sm:py-16 sm:py-20 lg:py-28 overflow-hidden bg-gradient-to-b from-[#FAFCFF] via-[#EDF0FC] to-[#FAFCFF]">
            {/* BG dots */}
            <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
                style={{ backgroundImage: "radial-gradient(circle, #3D5EE1 1.5px, transparent 1.5px)", backgroundSize: "40px 40px" }} />
            <div className="absolute top-[10%] -left-[10%] w-[500px] h-[500px] rounded-full pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(61,94,225,0.06) 0%, transparent 60%)", filter: "blur(60px)" }} />
            <div className="absolute bottom-[10%] -right-[10%] w-[500px] h-[500px] rounded-full pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(61,94,225,0.04) 0%, transparent 60%)", filter: "blur(60px)" }} />

            <div className="site-container flex flex-col items-center relative z-10">

                {/* ── Section Header ── */}
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
                    transition={{ duration: 0.7, ease: "easeOut" }}
                    data-testid="pricing-section-header"
                    className="flex flex-col items-center text-center w-full mb-12"
                >
                    <h1 data-testid="pricing-section-heading" className="font-heading font-bold md:font-light text-[22px] min-[380px]:text-[25px] sm:text-[34px] lg:text-[48px] leading-[1.35] tracking-tight text-[#0d1117]">
                        Invest in your school&apos;s{" "}
                        <span className="relative inline-block text-[#2040CF]">
                            future
                            <motion.svg
                                initial={{ pathLength: 0, opacity: 0 }}
                                animate={isInView ? { pathLength: 1, opacity: 1 } : { pathLength: 0, opacity: 0 }}
                                transition={{ duration: 1.5, delay: 0.5, ease: "easeInOut" }}
                                className="absolute -bottom-2 left-0 w-full" viewBox="0 0 100 10" fill="none"
                            >
                                <path d="M2 7 Q25 2 50 7 Q75 12 98 5" stroke="#3D5EE1" strokeWidth="4" strokeLinecap="round" strokeOpacity="0.3" />
                            </motion.svg>
                        </span>
                        {/* Empty in English; Hindi's verb lands after the
                            accent word, so the clause has to exist. */}
                        {t('pricing.headingAfter') && <>{" "}{t('pricing.headingAfter')}</>}
                    </h1>
                    <p data-testid="pricing-section-subheading" className="font-medium text-base md:text-lg text-[#596875] max-w-[620px] mt-6">
                        Subscribing to any plan instantly creates your school instance. Every plan includes the full ERP suite, no hidden feature gates.
                    </p>
                </motion.div>

                {/* ── Billing Cycle Toggle ── */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="flex justify-center mb-10 md:mb-14 w-full max-w-full px-2"
                >
                    <div data-testid="pricing-cycle-toggle-group" className="p-1 md:p-1.5 bg-white border border-[#3D5EE1]/15 rounded-[16px] md:rounded-[20px] shadow-[0_8px_32px_rgba(61,94,225,0.06)] inline-flex overflow-x-auto no-scrollbar max-w-full">
                        {BILLING_CYCLES.map((cycle, i) => (
                            <button key={cycle} onClick={() => setBillingCycle(cycle)}
                                data-testid={`pricing-cycle-tab-${cycle}`}
                                className={`px-3 py-2 md:px-5 md:py-2.5 rounded-xl md:rounded-2xl text-[12px] md:text-[14px] font-sora font-bold transition-all duration-300 whitespace-nowrap flex-shrink-0 ${billingCycle === cycle
                                    ? 'bg-gradient-to-r from-[#3D5EE1] to-[#1E3BB3] text-white shadow-[0_4px_16px_rgba(61,94,225,0.4)]'
                                    : 'text-[#596875] hover:text-[#1E3BB3] hover:bg-[#2A48C9]/5'
                                    }`}>
                                {cycleCopy?.[i]?.label}
                            </button>
                        ))}
                    </div>
                </motion.div>

                {/* ── Plan Cards ── */}
                {loading ? (
                    <div data-testid="pricing-loading-state" className="flex items-center gap-3 py-12 sm:py-16 sm:py-20">
                        <Loader2 size={28} className="animate-spin text-[#2040CF]" />
                        <span className="font-medium text-[#596875]">Loading plans…</span>
                    </div>
                ) : error ? (
                    <div data-testid="pricing-error-state" className="flex flex-col items-center gap-4 py-12 sm:py-16 sm:py-20">
                        <AlertCircle size={40} className="text-red-400" />
                        <p data-testid="pricing-error-message" className="text-[#596875] font-medium">{error}</p>
                        <Link href="/plans" data-testid="pricing-error-all-plans-link" className="px-6 py-2.5 rounded-xl bg-[#2040CF] text-white font-bold text-sm hover:bg-[#054E8A] transition-colors">
                            View All Plans
                        </Link>
                    </div>
                ) : displayPlans.length > 0 ? (
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                        transition={{ duration: 0.7, delay: 0.3 }}
                        data-testid="pricing-plans-grid"
                        className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch"
                    >
                        {displayPlans.map((plan) => {
                            const planKey = slug(plan.name) || plan._id;
                            const { price, salePrice, onSale } = getPriceDisplay(plan, billingCycle);
                            const activePrice = onSale ? salePrice : price;
                            const isPopular = plan.isMostPopular;
                            const isRecommended = plan.isRecommended;
                            const included = (plan.keyPoints || []).map(text => ({ text, included: true }));
                            const excluded = (plan.excludedFeatures || []).map(text => ({ text, included: false }));
                            const allFeatures = [...included, ...excluded];

                            return (
                                <div key={plan._id}
                                    data-testid={`pricing-plan-card-${planKey}`}
                                    className={`group relative flex flex-col p-4 sm:p-6 md:p-10 bg-white rounded-[20px] md:rounded-[32px] border-2 transition-all duration-500 hover:scale-[1.02] ${isPopular
                                        ? 'border-[#3D5EE1] shadow-[0_20px_60px_rgba(61,94,225,0.15)] scale-[1.02] z-10'
                                        : isRecommended
                                            ? 'border-[#12B76A] shadow-xl shadow-green-500/10'
                                            : 'border-[#E8F4FD] shadow-[0_8px_32px_rgba(61,94,225,0.05)] hover:border-[#3D5EE1]/30'
                                        }`}
                                >
                                    {isPopular && (
                                        <div data-testid={`pricing-plan-badge-popular-${planKey}`} className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-gradient-to-r from-[#2040CF] to-[#1B35AC] text-white text-[10px] font-black uppercase tracking-[0.2em] px-6 py-2 rounded-full shadow-[0_4px_16px_rgba(61,94,225,0.4)] z-20 whitespace-nowrap">
                                            Most Popular
                                        </div>
                                    )}
                                    {/* Badge fill is #047857, not the #12B76A
                                        used for the card's border. White on
                                        #12B76A is 2.62:1 — that green is bright
                                        enough to make a good border and far too
                                        light to carry white text, least of all
                                        at 10px. #047857 is the same hue dark
                                        enough to reach 5.6:1. The border keeps
                                        #12B76A: it is not text, so the contrast
                                        rule does not apply to it, and the accent
                                        stays recognisable. */}
                                    {isRecommended && !isPopular && (
                                        <div data-testid={`pricing-plan-badge-recommended-${planKey}`} className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#12B76A] text-white text-[10px] font-black uppercase tracking-[0.2em] px-6 py-2 rounded-full shadow-lg z-20 whitespace-nowrap">
                                            Recommended
                                        </div>
                                    )}

                                    {/* Plan name & desc */}
                                    <div className="text-left mb-4 md:mb-6">
                                        <h3 data-testid={`pricing-plan-name-${planKey}`} className={`font-sora text-lg md:text-2xl font-black mb-1 md:mb-2 ${isPopular ? 'text-[#2040CF]' : 'text-[#0d1117]'}`}>
                                            {plan.name}
                                        </h3>
                                        <p data-testid={`pricing-plan-description-${planKey}`} className="text-[#596875] text-[12px] md:text-[14px] leading-snug md:leading-relaxed font-medium min-h-[32px] md:min-h-[40px]">
                                            {plan.description}
                                        </p>
                                    </div>

                                    {/* Price */}
                                    <div data-testid={`pricing-plan-pricing-${planKey}`} className="mb-4 md:mb-8 text-left">
                                        <div className="flex items-baseline gap-1.5">
                                            <span data-testid={`pricing-plan-price-${planKey}`} className="font-sora text-3xl md:text-5xl font-black text-[#0d1117] tracking-tight">
                                                ₹{activePrice?.toLocaleString('en-IN') ?? '—'}
                                            </span>
                                            <span data-testid={`pricing-plan-price-suffix-${planKey}`} className="text-[#94a3b8] font-semibold text-sm md:text-lg">
                                                {cycleCopy?.[BILLING_CYCLES.indexOf(billingCycle)]?.suffix}
                                            </span>
                                        </div>
                                        {onSale && (
                                            <div className="flex items-center gap-2 mt-2">
                                                <span data-testid={`pricing-plan-price-original-${planKey}`} className="text-[#94a3b8] line-through font-semibold text-sm">₹{price?.toLocaleString('en-IN')}</span>
                                                <span data-testid={`pricing-plan-price-save-${planKey}`} className="text-[11px] font-black text-[#12B76A] bg-[#12B76A]/10 px-2 py-0.5 rounded-full">
                                                    SAVE {Math.round(((price - salePrice) / price) * 100)}%
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* CTA */}
                                    <Link href={`/auth/register-school?plan=${plan._id}&billing=${billingCycle}`}
                                        data-testid={`pricing-plan-cta-btn-${planKey}`}
                                        className={`w-full py-2.5 md:py-4 px-4 md:px-8 rounded-xl md:rounded-2xl text-[13px] md:text-[15px] font-bold transition-all mb-5 md:mb-8 text-center flex items-center justify-center gap-2 ${isPopular || isRecommended
                                            ? 'bg-gradient-to-r from-[#2040CF] to-[#1B35AC] text-white shadow-[0_8px_24px_rgba(61,94,225,0.3)] hover:shadow-[0_12px_32px_rgba(61,94,225,0.4)]'
                                            : 'bg-[#0d1117] text-white hover:bg-[#1a1a2e]'
                                            }`}>
                                        {t('pricing.choosePlan')}
                                        <CheckCircle2 className="w-5 h-5 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                                    </Link>

                                    {/* Features */}
                                    {allFeatures.length > 0 && (
                                        <div data-testid={`pricing-plan-features-${planKey}`} className="text-left space-y-2.5 md:space-y-4 flex-grow">
                                            <p data-testid={`pricing-plan-features-title-${planKey}`} className="text-[10px] md:text-[11px] font-black text-[#94a3b8] uppercase tracking-widest mb-1.5 md:mb-2">What&apos;s included</p>
                                            {allFeatures.map((feature, idx) => (
                                                <div key={idx}
                                                    data-testid={`pricing-plan-feature-${feature.included ? 'included' : 'excluded'}-${planKey}-${slug(feature.text)}`}
                                                    className="flex items-center gap-2.5 md:gap-3.5">
                                                    <div className={`flex-shrink-0 w-5 h-5 md:w-6 md:h-6 rounded-full flex items-center justify-center ${feature.included ? 'bg-[#3D5EE1]/10 text-[#2040CF]' : 'bg-gray-100 text-gray-300'}`}>
                                                        {feature.included
                                                            ? <Check className="w-3 h-3 md:w-3.5 md:h-3.5" strokeWidth={3} />
                                                            : <X className="w-3 h-3 md:w-3.5 md:h-3.5" strokeWidth={3} />
                                                        }
                                                    </div>
                                                    <span data-testid={`pricing-plan-feature-label-${planKey}-${slug(feature.text)}`} className={`text-[12px] md:text-[14px] font-medium ${feature.included ? 'text-[#4A5568]' : 'text-[#94a3b8] line-through decoration-2 opacity-60'}`}>
                                                        {feature.text}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </motion.div>
                ) : null}

                {/* ── Trust note & custom plan CTA ── */}
                {!loading && !error && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                        transition={{ duration: 0.6, delay: 0.5 }}
                        data-testid="pricing-footer-note"
                        className="mt-12 flex flex-col items-center gap-4"
                    >
                        <div data-testid="pricing-trust-note" className="flex items-center gap-2 text-[13px] text-[#6E87A0] font-medium">
                            <Shield size={14} />
                            <span>{t('pricing.trustNoCard')}</span>
                        </div>
                        <div data-testid="pricing-custom-plan-note" className="flex items-center gap-2 text-[13px] text-[#6E87A0] font-medium">
                            <Sparkles size={14} />
                            <span>
                                Need a custom plan?{" "}
                                <Link href="/contact" data-testid="pricing-contact-sales-link" className="font-bold text-[#3D5EE1] hover:text-[#1B35AC] underline underline-offset-4 decoration-[#3D5EE1]/40 hover:decoration-[#1B35AC] transition-colors">
                                    Contact our sales team
                                </Link>
                            </span>
                        </div>
                    </motion.div>
                )}

            </div>
        </section>
    );
}

